import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { setTaskProgress } from '../../pages/home/Projects/api/projectOperations.js';
import { setTaskFeedback, setTaskDate } from '../../pages/home/Projects/api/projectOperations.js';

// Helpers
const makeKey = (projectId, userId) => `${projectId || ''}_${userId || ''}`;

// Thunks
export const updateTaskStatus = createAsyncThunk(
  'projectProgress/updateTaskStatus',
  async ({ projectId, userId, phaseId, taskId, status }, thunkAPI) => {
    await setTaskProgress(projectId, userId, phaseId, taskId, status);
    return { projectId, userId, phaseId, taskId, status };
  }
);

export const toggleTaskFeedback = createAsyncThunk(
  'projectProgress/toggleTaskFeedback',
  async ({ projectId, userId, phaseId, taskId, value }, thunkAPI) => {
    await setTaskFeedback(projectId, userId, phaseId, taskId, value);
    return { projectId, userId, phaseId, taskId, value };
  }
);

export const updateTaskDate = createAsyncThunk(
  'projectProgress/updateTaskDate',
  async ({ projectId, userId, phaseId, taskId, iso }, thunkAPI) => {
    await setTaskDate(projectId, userId, phaseId, taskId, iso);
    return { projectId, userId, phaseId, taskId, iso };
  }
);

const initialState = {
  // byKey maps `${projectId}_${userId}` -> { phases: { [phaseId]: status }, tasks: { [`${phaseId}:${taskId}`]: status }, feedback: { [key]: boolean } }
  byKey: {},
};

const ensure = (state, projectId, userId) => {
  const k = makeKey(projectId, userId);
  if (!state.byKey[k]) state.byKey[k] = { phases: {}, tasks: {}, feedback: {}, dates: {} };
  return state.byKey[k];
};

const projectProgressSlice = createSlice({
  name: 'projectProgress',
  initialState,
  reducers: {
    upsertProgress(state, action) {
      const { projectId, userId, phases = {}, tasks = {}, feedback = {}, dates = {} } = action.payload || {};
      const entry = ensure(state, projectId, userId);
      // Merge shallowly
      entry.phases = { ...entry.phases, ...phases };
      entry.tasks = { ...entry.tasks, ...tasks };
      entry.feedback = { ...entry.feedback, ...feedback };
      entry.dates = { ...entry.dates, ...dates };
    },
    clearProgressFor(state, action) {
      const { projectId, userId } = action.payload || {};
      const k = makeKey(projectId, userId);
      delete state.byKey[k];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateTaskStatus.fulfilled, (state, action) => {
        const { projectId, userId, phaseId, taskId, status } = action.payload;
        const entry = ensure(state, projectId, userId);
        const key = `${phaseId}:${taskId}`;
        entry.tasks[key] = status;
      })
      .addCase(toggleTaskFeedback.fulfilled, (state, action) => {
        const { projectId, userId, phaseId, taskId, value } = action.payload;
        const entry = ensure(state, projectId, userId);
        const key = `${phaseId}:${taskId}`;
        entry.feedback[key] = !!value;
      })
      .addCase(updateTaskDate.fulfilled, (state, action) => {
        const { projectId, userId, phaseId, taskId, iso } = action.payload;
        const entry = ensure(state, projectId, userId);
        const key = `${phaseId}:${taskId}`;
        entry.dates[key] = iso || null;
      });
  }
});

export const { upsertProgress, clearProgressFor } = projectProgressSlice.actions;
export default projectProgressSlice.reducer;

// Selectors
export const selectProgressFor = (state, projectId, userId) => state.projectProgress?.byKey?.[makeKey(projectId, userId)] || { phases: {}, tasks: {}, feedback: {} };
export const selectTaskStatus = (state, projectId, userId, phaseId, taskId) => {
  const entry = selectProgressFor(state, projectId, userId);
  return entry.tasks?.[`${phaseId}:${taskId}`];
};
export const selectTaskFeedback = (state, projectId, userId, phaseId, taskId) => {
  const entry = selectProgressFor(state, projectId, userId);
  return !!entry.feedback?.[`${phaseId}:${taskId}`];
};
export const selectTaskDate = (state, projectId, userId, phaseId, taskId) => {
  const entry = selectProgressFor(state, projectId, userId);
  return entry.dates?.[`${phaseId}:${taskId}`] || null;
};
